import { useEffect, useMemo, useRef, useState } from 'react'
import {
  startOfWeek,
  addDays,
  addMinutes,
  startOfDay,
  differenceInCalendarDays,
  isSameDay,
  format,
} from 'date-fns'
import { getEventGradient } from '../utils/calendarEventColors'

type WeekGridEvent = {
  id: string
  title: string
  start: Date
  end: Date
  tags?: string[] | string | null
}

type CalendarWeekGridProps = {
  weekDate: Date
  events: WeekGridEvent[]
  onSelectEvent?: (id: string, rect?: DOMRect) => void
  onContextMenuSlot?: (date: Date, anchor: { x: number; y: number }) => void
}

const MINUTE_HEIGHT = 1
const HOUR_HEIGHT = 60 * MINUTE_HEIGHT
const DAY_HEIGHT = 24 * HOUR_HEIGHT
const SECTIONS = [0, 1, 2]

const formatHour = (hour: number) => {
  if (hour === 0) return '12:00 AM'
  if (hour === 12) return '12:00 PM'
  if (hour < 12) return `${hour}:00 AM`
  return `${hour - 12}:00 PM`
}

function CalendarWeekGrid({ weekDate, events, onSelectEvent, onContextMenuSlot }: CalendarWeekGridProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const [now, setNow] = useState(() => new Date())

  const weekStart = useMemo(() => startOfWeek(weekDate), [weekDate])
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  )

  const eventsByDay = useMemo(() => {
    const grouped = new Map<number, WeekGridEvent[]>()
    for (let i = 0; i < 7; i += 1) grouped.set(i, [])
    events.forEach((event) => {
      const dayIndex = differenceInCalendarDays(startOfDay(event.start), weekStart)
      if (dayIndex >= 0 && dayIndex < 7) {
        grouped.get(dayIndex)?.push(event)
      }
    })
    grouped.forEach((list) => list.sort((a, b) => a.start.getTime() - b.start.getTime()))
    return grouped
  }, [events, weekStart])

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const todayIndex = useMemo(() => differenceInCalendarDays(startOfDay(now), weekStart), [now, weekStart])
  const nowMinutes = now.getHours() * 60 + now.getMinutes()

  useEffect(() => {
    const scroller = scrollRef.current
    if (!scroller) return

    const setInitial = () => {
      const allMinutes = events.map(ev => ev.start.getHours() * 60 + ev.start.getMinutes()).sort((a, b) => a - b)
      
      let targetMinutes: number
      if (allMinutes.length > 0) {
        // Use median event time
        targetMinutes = allMinutes[Math.floor(allMinutes.length / 2)]
      } else {
        // Fallback to current time
        const now = new Date()
        targetMinutes = now.getHours() * 60 + now.getMinutes()
      }

      const baseTop = DAY_HEIGHT + targetMinutes * MINUTE_HEIGHT
      const centered = Math.max(0, baseTop - scroller.clientHeight / 2)
      scroller.scrollTop = centered
    }

    requestAnimationFrame(setInitial)
  }, [weekDate, events])

  useEffect(() => {
    const scroller = scrollRef.current
    if (!scroller) return

    let adjusting = false
    const onScroll = () => {
      if (adjusting) return
      const buffer = DAY_HEIGHT * 0.3
      if (scroller.scrollTop < buffer) {
        adjusting = true
        scroller.scrollTop += DAY_HEIGHT
        requestAnimationFrame(() => {
          adjusting = false
        })
      } else if (scroller.scrollTop + scroller.clientHeight > DAY_HEIGHT * 3 - buffer) {
        adjusting = true
        scroller.scrollTop -= DAY_HEIGHT
        requestAnimationFrame(() => {
          adjusting = false
        })
      }
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => scroller.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="week-grid">
      <div className="week-grid-header">
        <div className="week-grid-time-head" />
        {weekDays.map((day) => (
          <div key={day.toISOString()} className="week-grid-day-head">
            <span className="calendar-week-header-day">{format(day, 'EEE').toUpperCase()}</span>
            <span className="calendar-week-header-date">{format(day, 'd')}</span>
          </div>
        ))}
      </div>
      <div
        className="week-grid-scroll"
        ref={scrollRef}
        onContextMenu={(e) => {
          if (!onContextMenuSlot) return
          e.preventDefault()
          const body = bodyRef.current
          if (!body) return
          const bodyRect = body.getBoundingClientRect()
          const leftOffset = e.clientX - bodyRect.left
          const topOffset = e.clientY - bodyRect.top + (scrollRef.current?.scrollTop || 0)
          const dayWidth = (body.clientWidth - 84) / 7
          const dayIndex = Math.max(0, Math.min(6, Math.floor((leftOffset - 84) / dayWidth)))
          const relativeY = Math.max(0, topOffset % DAY_HEIGHT)
          const minutes = Math.max(0, Math.min(24 * 60 - 1, Math.round(relativeY / MINUTE_HEIGHT)))
          const date = addMinutes(startOfDay(addDays(weekStart, dayIndex)), minutes)
          onContextMenuSlot(date, { x: e.clientX, y: e.clientY + window.scrollY })
        }}
      >
        <div className="week-grid-body" ref={bodyRef} style={{ height: `${DAY_HEIGHT * 3}px` }}>
          <div className="week-grid-time-col">
            {SECTIONS.map((section) =>
              Array.from({ length: 24 }, (_, hour) => {
                const top = section * DAY_HEIGHT + hour * HOUR_HEIGHT
                return (
                  <div key={`time-${section}-${hour}`} className="week-grid-time-label" style={{ top: `${top}px` }}>
                    {formatHour(hour)}
                  </div>
                )
              }),
            )}
          </div>

          <div className="week-grid-columns">
            {weekDays.map((day, dayIndex) => {
              const isToday = isSameDay(day, new Date())
              return (
                <div key={`col-${day.toISOString()}`} className={`week-grid-col ${isToday ? 'today' : ''}`}>
                  {SECTIONS.map((section) =>
                    Array.from({ length: 24 }, (_, hour) => {
                      const top = section * DAY_HEIGHT + hour * HOUR_HEIGHT
                      const halfTop = top + HOUR_HEIGHT / 2
                      return (
                        <div key={`line-group-${section}-${hour}`}>
                          <div className="week-grid-hour-line" style={{ top: `${top}px` }} />
                          <div className="week-grid-half-hour-line" style={{ top: `${halfTop}px` }} />
                        </div>
                      )
                    }),
                  )}

                  {SECTIONS.map((section) =>
                    (eventsByDay.get(dayIndex) || []).map((event) => {
                      const startMinutes = event.start.getHours() * 60 + event.start.getMinutes()
                      const rawEndMinutes = event.end.getHours() * 60 + event.end.getMinutes()
                      const endMinutes = rawEndMinutes <= startMinutes ? startMinutes + 30 : rawEndMinutes
                      const top = section * DAY_HEIGHT + startMinutes * MINUTE_HEIGHT
                      const height = Math.max((endMinutes - startMinutes) * MINUTE_HEIGHT, 26)
                      // Use a conservative chars-per-line estimate so long titles in narrow columns fully expand.
                      const estimatedLines = Math.max(1, Math.ceil(event.title.length / 10))
                      const expandedHeight = Math.max(height, Math.min(230, estimatedLines * 19 + 16))
                      const gradient = getEventGradient(event.tags)
                      return (
                        <button
                          type="button"
                          key={`${section}-${event.id}`}
                          className="week-grid-event"
                          style={{
                            top: `${top}px`,
                            height: `${height}px`,
                            ['--base-height' as string]: `${height}px`,
                            ['--expanded-height' as string]: `${expandedHeight}px`,
                            ['--week-event-bg' as string]: gradient,
                            ['--event-pill-gradient' as string]: gradient,
                          }}
                          onClick={(evt) => onSelectEvent?.(event.id, evt.currentTarget.getBoundingClientRect())}
                        >
                          <span className="week-grid-event-title">{event.title}</span>
                        </button>
                      )
                    }),
                  )}
                  {dayIndex === todayIndex
                    ? SECTIONS.map((section) => {
                        const lineTop = section * DAY_HEIGHT + nowMinutes * MINUTE_HEIGHT
                        return (
                          <div
                            key={`now-${section}`}
                            className="week-grid-now-line"
                            style={{ top: `${lineTop}px` }}
                          >
                            <span className="week-grid-now-dot" />
                          </div>
                        )
                      })
                    : null}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CalendarWeekGrid
